import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';
import { supabase } from '@/lib/supabase';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useAuthContext } from '@/contexts/AuthContext';

type Range = '7d' | '14d' | '30d';

interface DailyPoint {
  date: string;
  requests: number;
  errors: number;
  latency: number;
}

interface RawRow {
  request_date: string;
  request_count: number;
  error_count: number;
  avg_latency_ms: number;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildEmptyDays(days: number): DailyPoint[] {
  return Array.from({ length: days }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1 - i));
    return {
      date: formatDate(d.toISOString().slice(0, 10)),
      requests: 0,
      errors: 0,
      latency: 0,
    };
  });
}

export default function UsageStats() {
  const { user } = useAuthContext();
  const { subscriptions, loading: subsLoading } = useSubscriptions();
  const activeApis = subscriptions.filter(s => s.status === 'active');

  const [range, setRange] = useState<Range>('7d');
  const [chartData, setChartData] = useState<DailyPoint[]>([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  const rangeDays = range === '7d' ? 7 : range === '14d' ? 14 : 30;

  const fetchUsage = useCallback(async () => {
    if (!user) return;
    setLoadingChart(true);

    const since = new Date();
    since.setDate(since.getDate() - (rangeDays - 1));
    const sinceStr = since.toISOString().slice(0, 10);

    const { data } = await supabase
      .from('api_usage_daily')
      .select('request_date, request_count, error_count, avg_latency_ms')
      .eq('user_id', user.id)
      .gte('request_date', sinceStr)
      .order('request_date', { ascending: true });

    const skeleton = buildEmptyDays(rangeDays);

    if (data && data.length > 0) {
      const byDate: Record<string, { req: number; err: number; lat: number; count: number }> = {};
      (data as RawRow[]).forEach(r => {
        const key = formatDate(r.request_date);
        if (!byDate[key]) byDate[key] = { req: 0, err: 0, lat: 0, count: 0 };
        byDate[key].req += r.request_count;
        byDate[key].err += r.error_count;
        byDate[key].lat += r.avg_latency_ms;
        byDate[key].count += 1;
      });

      const filled = skeleton.map(pt => {
        const d = byDate[pt.date];
        if (!d) return pt;
        return {
          date: pt.date,
          requests: d.req,
          errors: d.err,
          latency: d.count > 0 ? Math.round(d.lat / d.count) : 0,
        };
      });
      setChartData(filled);
    } else {
      setChartData(skeleton);
    }

    setLastRefreshed(new Date());
    setLoadingChart(false);
  }, [user, rangeDays]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  // Realtime subscription — refresh chart when new usage rows land
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('usage-daily-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'api_usage_daily', filter: `user_id=eq.${user.id}` }, () => {
        fetchUsage();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchUsage]);

  const totalRequests = activeApis.reduce((a, b) => a + (b.requests_used || 0), 0);
  const chartTotal = chartData.reduce((a, b) => a + b.requests, 0);
  const chartErrors = chartData.reduce((a, b) => a + b.errors, 0);
  const successRate = chartTotal > 0 ? (((chartTotal - chartErrors) / chartTotal) * 100).toFixed(1) : '100.0';
  const avgLatency = chartData.filter(d => d.latency > 0).length > 0
    ? Math.round(chartData.filter(d => d.latency > 0).reduce((a, b) => a + b.latency, 0) / chartData.filter(d => d.latency > 0).length)
    : 0;

  if (subsLoading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="w-9 h-9 bg-gray-100 rounded-lg mb-3" />
              <div className="h-6 bg-gray-100 rounded w-20 mb-1" />
              <div className="h-3 bg-gray-100 rounded w-24" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl p-5 h-64 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: `Requests (${range})`, value: chartTotal.toLocaleString(), icon: 'ri-terminal-line', color: 'text-brand-500 bg-brand-50' },
          { label: 'Success Rate', value: `${successRate}%`, icon: 'ri-shield-check-line', color: 'text-green-600 bg-green-50' },
          { label: 'Avg Latency', value: avgLatency > 0 ? `${avgLatency}ms` : '—', icon: 'ri-timer-line', color: 'text-amber-500 bg-amber-50' },
          { label: 'Active APIs', value: activeApis.length, icon: 'ri-link-m', color: 'text-slate-600 bg-slate-100' },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="bg-white rounded-xl p-4">
            <span className={`w-9 h-9 flex items-center justify-center rounded-lg mb-3 ${s.color}`}><i className={`${s.icon} text-lg`} /></span>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Main Chart */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">Request Volume</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Total API calls per day · Last refreshed {lastRefreshed.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Range selector */}
            <div className="flex items-center gap-1 bg-surface-2 border border-surface-3 rounded-lg p-1">
              {(['7d', '14d', '30d'] as Range[]).map(r => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md cursor-pointer whitespace-nowrap transition-colors ${range === r ? 'bg-sidebar text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <button
              onClick={fetchUsage}
              disabled={loadingChart}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-surface-3 rounded-lg text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
            >
              <i className={`ri-refresh-line ${loadingChart ? 'animate-spin' : ''}`} />Refresh
            </button>
          </div>
        </div>

        {loadingChart ? (
          <div className="h-52 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-300" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff7f0a" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#ff7f0a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="errGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={range === '30d' ? 4 : range === '14d' ? 1 : 0} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: '1px solid #f0f0f0', fontSize: 12, boxShadow: 'none' }}
                formatter={(val: number, name: string) => [val.toLocaleString(), name]}
              />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="requests" name="Requests" stroke="#ff7f0a" strokeWidth={2} fill="url(#reqGrad)" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="errors" name="Errors" stroke="#ef4444" strokeWidth={1.5} fill="url(#errGrad)" dot={false} activeDot={{ r: 3 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Latency Chart */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-1">Latency Trend</h3>
          <p className="text-xs text-gray-400 mb-4">Average response time per day (ms)</p>
          {loadingChart ? (
            <div className="h-40 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-xl text-gray-300" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barSize={range === '30d' ? 6 : range === '14d' ? 10 : 18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={range === '30d' ? 4 : range === '14d' ? 1 : 0} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} unit="ms" />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12, boxShadow: 'none' }} formatter={v => [`${v}ms`, 'Latency']} />
                <Bar dataKey="latency" fill="#1e293b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Per-subscription breakdown */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-1">Usage by Subscription</h3>
          <p className="text-xs text-gray-400 mb-4">Lifetime request allocation per active plan</p>
          {activeApis.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <i className="ri-bar-chart-2-line text-3xl text-gray-200 mb-2" />
              <p className="text-xs text-gray-400">No active subscriptions yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeApis.map((sub) => {
                const pct = sub.requests_limit > 0 ? Math.min(100, Math.round((sub.requests_used / sub.requests_limit) * 100)) : 0;
                return (
                  <div key={sub.id}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="font-medium text-gray-700 truncate max-w-[55%]">{sub.product_name}</span>
                      <span className="text-gray-400 shrink-0">{sub.requests_used.toLocaleString()} / {sub.requests_limit.toLocaleString()}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-brand-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-gray-400">{pct}% used</span>
                      <span className="text-[11px] text-gray-400">{sub.pricing_type.replace(/_/g, '-')} plan</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* All-time total */}
          {activeApis.length > 0 && (
            <div className="mt-5 pt-4 border-t border-surface-3 flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Total lifetime requests</span>
              <span className="text-sm font-bold text-gray-900">{totalRequests.toLocaleString()}</span>
            </div>
          )}
        </motion.div>
      </div>

      {/* Realtime indicator */}
      <div className="flex items-center gap-2 text-[11px] text-gray-400">
        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
        Live · auto-updates when new requests are recorded
      </div>
    </div>
  );
}
